import { useEffect, useMemo, useState } from 'react';
import type { CwTicketStatusAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import {
  useCwProjectTicketStatusAliases,
  useSetCwProjectTicketStatusAliases,
} from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwStatusName: string;
  ticketStatusId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `project-ticket-status-alias-${aliasSeq}`;
}

interface ProjectTicketStatusAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise PROJECT ticket status (a dropdown) onto a PSA ticket status (a
 * dropdown). Project tickets use a different CW status list than service tickets, so
 * they resolve their status through these rows. A CW project status with no row here
 * falls back to the default PSA ticket status, so the sync never fails on a missing
 * alias.
 */
export function ProjectTicketStatusAliasEditor({
  isOpen,
}: ProjectTicketStatusAliasEditorProps): JSX.Element {
  const configQuery = useCwProjectTicketStatusAliases(isOpen);
  const saveAliases = useSetCwProjectTicketStatusAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwStatusName: alias.cwStatusName,
        ticketStatusId: alias.ticketStatusId,
      })),
    );
  }, [config]);

  const ticketStatuses = config?.ticketStatuses ?? [];
  const discoveredStatuses = config?.discoveredStatuses ?? [];
  const live = config?.live ?? false;

  // CW status names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwStatusName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredStatuses.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwStatusName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwStatusName, ticketStatusId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwTicketStatusAliasDto[] = rows
      .filter((row) => row.cwStatusName.trim() && row.ticketStatusId)
      .map((row) => ({
        cwStatusName: row.cwStatusName.trim(),
        ticketStatusId: row.ticketStatusId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-project-ticket-statuses';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Project ticket status</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise project tickets use a different status list than service tickets. Map each
          ConnectWise project ticket status to a PSA ticket status below. On sync, each project
          ticket&apos;s status is set from its mapped PSA status — ConnectWise statuses without a
          row here fall back to the default PSA ticket status.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading project ticket statuses…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load project ticket statuses.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise project ticket statuses loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise project ticket statuses discovered yet. Configure the connection to
              load suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredStatuses.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the project-ticket-status aliases. Make sure each row targets a valid
              ticket status.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">
              No project-ticket-status aliases yet. Add one below.
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise status</th>
                  <th className="py-2 pr-3 font-medium">PSA ticket status</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwStatusName}
                        placeholder="e.g. Planning"
                        onChange={(e) => updateRow(row.id, { cwStatusName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.ticketStatusId}
                        onChange={(e) => updateRow(row.id, { ticketStatusId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select status —</option>
                        {ticketStatuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save project statuses
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
